/**
 * run-texts — teksty o zadaniu przycisku SQL, wspólne dla przycisku, paska blokady i komunikatów.
 *
 * Wszystkie przechodzą przez i18n (`recordProcedureRuns.*`); z serwera pochodzą wyłącznie dane
 * (napis wyniku, tekst błędu, nazwa osoby), które jako gotowe polskie zdania wstawiamy bez zmian.
 */

import type { TFunction } from 'i18next';

import { formatRunClock, type RecordProcedureRun, type RunConflict, runSinceIso } from './runs-api';

/**
 * Napis przycisku zadania: napis klocka z układu, a gdy układ go nie zna (zadanie z innego układu) —
 * nazwa procedury z katalogu.
 */
export function runLabel(
  run: RecordProcedureRun,
  labelsByProcedureId: ReadonlyMap<string, string>,
): string {
  return labelsByProcedureId.get(run.procedureId) ?? run.procedureName;
}

/**
 * Tekst wyniku zadania w stanie końcowym: `done` → komunikat procedury albo „Wykonano: <napis>”,
 * `failed` → tekst błędu z serwera, `interrupted` → opis przerwania z serwera. Stany aktywne nie mają
 * wyniku (pusty tekst).
 */
export function runResultText(run: RecordProcedureRun, label: string, t: TFunction): string {
  switch (run.status) {
    case 'done':
      return run.message ?? t('recordProcedureRuns.doneDefault', { label });
    case 'failed':
      return run.error ?? t('recordProcedureRuns.failedDefault');
    case 'interrupted':
      return run.message ?? t('recordProcedureRuns.interruptedDefault');
    case 'queued':
    case 'running':
      return '';
  }
}

/** Zdanie o blokadzie rekordu — ten sam tekst na pasku nad formularzem i w komunikacie po odrzuconym zapisie. */
export function runLockText(
  run: RecordProcedureRun,
  label: string,
  t: TFunction,
  locale: string,
): string {
  const time = formatRunClock(runSinceIso(run), locale);
  const person = run.requestedByName ?? null;
  return person !== null
    ? t('recordProcedureRuns.lockBanner.text', { label, time, person })
    : t('recordProcedureRuns.lockBanner.textNoPerson', { label, time });
}

/**
 * Komunikat o odrzuconym zleceniu albo zapisie / usunięciu rekordu (kody z `extractRunConflict`).
 * `RUN_IN_PROGRESS` nazywa trwające zadanie napisem jego przycisku (to może być zadanie INNEGO
 * przycisku niż kliknięty); bez aktywnego zadania w odpowiedzi wraca do ogólnego zdania.
 * `RECORD_LOCKED_BY_RUN` bez aktywnego zadania w odpowiedzi (zadanie właśnie się skończyło) wraca do
 * gotowego komunikatu serwera, a gdy go nie ma — do ogólnego zdania o blokadzie.
 */
export function runConflictText(
  conflict: RunConflict,
  labelsByProcedureId: ReadonlyMap<string, string>,
  t: TFunction,
  locale: string,
): string {
  const { activeRun } = conflict;
  switch (conflict.code) {
    case 'PERMISSION_DENIED':
      return t('recordProcedureRuns.errors.permissionDenied');
    case 'PROCEDURE_UNAVAILABLE':
      return t('recordProcedureRuns.errors.procedureUnavailable');
    case 'RUN_IN_PROGRESS': {
      if (activeRun === null) return t('recordProcedureRuns.errors.inProgressUnknown');
      const label = runLabel(activeRun, labelsByProcedureId);
      const time = formatRunClock(runSinceIso(activeRun), locale);
      const person = activeRun.requestedByName ?? null;
      return person !== null
        ? t('recordProcedureRuns.errors.inProgress', { label, time, person })
        : t('recordProcedureRuns.errors.inProgressNoPerson', { label, time });
    }
    case 'RECORD_LOCKED_BY_RUN':
      if (activeRun !== null) {
        return runLockText(activeRun, runLabel(activeRun, labelsByProcedureId), t, locale);
      }
      return conflict.message ?? t('recordProcedureRuns.errors.lockedGeneric');
  }
}
